// Calendar.js - Huvudkomponent för kalender med veckovy och dagsvy

import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAppointments, getAppointment, getAppointmentTypes, cancelAppointment, endSeries, occurrenceDateOf, getClinicUsers, getBlocks, deleteBlock, getScheduleBlocks, updateAppointment, updateBlock, getEffectiveSlots, createSlotOverride, deleteSlotOverride, registerNoShow, ensureRealAppointment, getOccurrence, appointmentKey, isSeriesAppointment, isVirtualOccurrence, moveOccurrence } from './calendarApi';
import { UserContext } from '../../UserContext';
import apiClient from '../../apiClient';
import AppointmentModal from './AppointmentModal';
import { textColorForBackground } from './colorUtils';
import { bookedByText } from './bookedBy';
import DateJumpPicker, { isoWeekNumber, isoWeekYear, mondayOf } from './DateJumpPicker';
import PaymentModal from './PaymentModal';
import KallelseModal from './KallelseModal';
import BlockNoteModal from './BlockNoteModal';
import GroupParticipantsModal from './GroupParticipantsModal';
import AvailableSlotSearch from './AvailableSlotSearch';
import CalendarLegend from './CalendarLegend';
import { copyAppointment, getCopiedAppointment, copiedLabel, pasteAppointment } from './calendarClipboard';
import BehandlareSjukModal from './BehandlareSjukModal';
import DayView from './DayView';
import SlotOverrideModal from './SlotOverrideModal';
import useContextMenuPosition from './useContextMenuPosition';
import { uteblivetBekraftelseText } from './uteblivetApi';
import { useActivePatient } from '../../patient/ActivePatientContext';
import CancelledMarker, { groupCancelledByStart, applyCancelledGutter } from './CancelledMarker';
import './Calendar.css';

const DAYS = ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag'];
const START_HOUR = 7;
const END_HOUR = 19;

// Drag-and-drop konstanter
// Vertikal skala (Spår 1, 2026-09-19): ENDA sanningskällan för minut→px.
// Allt som positionerar i rutnätet (slothöjd, bokningar, block, avbokad-markör,
// schemaremsor, nu-linje) multiplicerar minuter med denna; drag delar med den.
const PIXELS_PER_MINUTE = 2;
// Drag och ändrad längd snappar alltid i 5-minuterssteg, oberoende av vald
// visningsupplösning (Spår S1). Visningen styr bara rutnätets linjer.
const DRAG_SNAP_MINUTES = 5;

// Unik nyckel per block-tillfälle: återkommande block delar seriens block_id
const blockKey = (block) => `${block.block_id}@${block.start_time}`;

// Minuter på dygnet → "HH:MM"
const formatMinutes = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

// Generera tidsslots baserat på intervall (i minuter)
const generateTimeSlots = (intervalMinutes) => {
  const slots = [];
  for (let hour = START_HOUR; hour < END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      slots.push({ hour, minute });
    }
  }
  return slots;
};

// Formatera tid för visning
const formatSlotTime = (slot) => {
  return `${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}`;
};

// Hjälpfunktion för lokal datumformatering (undvik tidszonsproblem med toISOString)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Calendar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useContext(UserContext);

  // Vy-state: 'week' eller 'day'
  const [currentView, setCurrentView] = useState('week');

  const [currentWeek, setCurrentWeek] = useState(getWeekStart(new Date()));
  const [appointments, setAppointments] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Behandlar-filter
  // Användare med kalender + ev. användare utan kalender som ändå har bokningar
  // denna vecka (has_calendar=false → visas nedtonade, inga nya bokningar)
  const [clinicUsers, setClinicUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(''); // Sätts till inloggad användare i useEffect

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAppointment, setPaymentAppointment] = useState(null);

  // Kallelse modal state
  const [showKallelseModal, setShowKallelseModal] = useState(false);
  const [kallelseAppointment, setKallelseAppointment] = useState(null);
  const [preSelectedKallelseTemplate, setPreSelectedKallelseTemplate] = useState(null);

  // Block/Note modal state
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockModalMode, setBlockModalMode] = useState('block'); // 'block' eller 'note'
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [blocks, setBlocks] = useState([]);

  // Schedule blocks state (för visuell markering av arbetstider)
  const [scheduleBlocks, setScheduleBlocks] = useState([]);

  // Schemamall-slots per datum: { 'YYYY-MM-DD': [slot, ...] }
  const [scheduleSlots, setScheduleSlots] = useState({});
  // Info-popup vid klick på en schemaslot: { x, y, slot }
  const [activeSlotInfo, setActiveSlotInfo] = useState(null);
  // Förvald bokningstyp när man bokar på en schemaslot (typ + slot-längd)
  const [preSelectedSlotType, setPreSelectedSlotType] = useState(null);
  // Slot-override-modal (lägg till/ändra schemaslot en specifik dag)
  const [slotModal, setSlotModal] = useState(null); // { mode, date, slot }

  // Group participants modal state
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupAppointment, setGroupAppointment] = useState(null);

  // Available slot search modal state
  const [showSlotSearch, setShowSlotSearch] = useState(false);
  // Aktiv patient (Spår 3): högerklick "Boka ny tid" gör bokningens patient aktiv
  // så AppointmentModal förväljer den när en ledig tid valts.
  const { setActivePatient } = useActivePatient() || {};

  // Behandlare sjuk modal state
  const [showBehandlareSjukModal, setShowBehandlareSjukModal] = useState(false);

  // Klinik-inställningar för journalmodul
  const [useInternalJournal, setUseInternalJournal] = useState(false);

  // Tidsintervall för slots (minuter): 60, 30, 15, eller egen
  const [slotInterval, setSlotInterval] = useState(30);

  // Drag-and-drop state
  const [dragState, setDragState] = useState({
    isDragging: false,
    itemType: null, // 'appointment' eller 'block'
    itemId: null,
    itemData: null,
    mode: 'move', // 'move' eller 'resize' (ändra längd, Spår K3)
    startY: 0,
    currentY: 0,
    startX: 0,
    currentX: 0,
    originalTop: 0,
    dayIndex: null // Vilken dag som dras
  });

  // Refs för drag-and-drop
  const dragRef = useRef(null);
  const justDraggedRef = useRef(false);

  // Generera tidsslots baserat på intervall
  const timeSlots = useMemo(() => generateTimeSlots(slotInterval), [slotInterval]);

  // Beräkna slothöjd baserat på intervall och vertikal skala
  const slotHeight = slotInterval * PIXELS_PER_MINUTE;

  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    appointment: null,
    block: null,
    slotDate: null
  });

  // Ref + klampad position så högerklicksmenyn aldrig klipps av mot skärmkanten (Spår B)
  const contextMenuRef = useRef(null);
  const contextMenuPos = useContextMenuPosition(
    contextMenuRef, contextMenu.visible, contextMenu.x, contextMenu.y
  );

  // Markerad bokning (enkelklick markerar, dubbelklick öppnar)
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);

  // Nu-linje (Spår 6): aktuell tid, tickar en gång per minut
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);
  const nowTop = (now.getHours() * 60 + now.getMinutes()) - START_HOUR * 60;
  const nowVisible = nowTop >= 0 && nowTop <= (END_HOUR - START_HOUR) * 60;
  const nowTopPx = nowTop * PIXELS_PER_MINUTE;

  // Hämta veckans start (måndag)
  function getWeekStart(date) {
    return mondayOf(new Date(date));
  }

  // Hämta datum för varje dag i veckan
  const getWeekDates = useCallback(() => {
    return DAYS.map((_, index) => {
      const date = new Date(currentWeek);
      date.setDate(date.getDate() + index);
      return date;
    });
  }, [currentWeek]);

  // Ladda användare för kliniken - bara de med kalender, plus de utan kalender
  // som har bokningar den visade veckan (så att bokningarna inte blir osynliga)
  useEffect(() => {
    let stale = false; // snabb bläddring: ett äldre svar får inte skriva över
    const loadUsers = async () => {
      try {
        const weekEnd = new Date(currentWeek);
        weekEnd.setDate(weekEnd.getDate() + 7);
        const users = await getClinicUsers({
          calendarOnly: true,
          from: formatLocalDate(currentWeek),
          to: formatLocalDate(weekEnd)
        });
        if (stale) return;
        // Filtrera på samma klinik som inloggad användare
        const filteredUsers = users.filter(u => u.clinic_id === currentUser?.clinic_id);
        setClinicUsers(filteredUsers);
        setUsersLoaded(true);
      } catch (err) {
        console.error('Error loading users:', err);
      }
    };
    if (currentUser) {
      loadUsers();
    }
    return () => { stale = true; };
  }, [currentUser, currentWeek]);

  // Förvald behandlare: inloggad användare om hen har kalender, annars första
  // användaren med kalender. Väljs om även när vald användare försvinner ur
  // listan (t.ex. ett konto utan kalender när veckan saknar bokningar).
  useEffect(() => {
    if (!usersLoaded) return;
    if (clinicUsers.some(u => String(u.user_id) === selectedUserId)) return;
    const calendarUsers = clinicUsers.filter(u => u.has_calendar !== false);
    const next = calendarUsers.find(u => u.user_id === currentUser?.user_id) || calendarUsers[0];
    if (next) {
      setSelectedUserId(String(next.user_id));
    } else {
      setLoading(false); // ingen behandlare med kalender - släck spinnern
    }
  }, [usersLoaded, clinicUsers, selectedUserId, currentUser]);

  // Vald behandlare saknar kalender → nedtonad vy, inga nya bokningar
  const selectedCalendarDisabled = clinicUsers.some(
    u => String(u.user_id) === selectedUserId && u.has_calendar === false
  );

  // Öppna gruppmodal direkt vid navigering från startsidan
  // (navigate('/calendar', { state: { openGroupAppointmentId, date } }))
  useEffect(() => {
    const groupId = location.state?.openGroupAppointmentId;
    if (!groupId) return;

    const openFromState = async () => {
      try {
        let appointment = await getAppointment(groupId);
        const dateStr = location.state?.date ? String(location.state.date).slice(0, 10) : null;
        if (dateStr) {
          setCurrentWeek(getWeekStart(new Date(location.state.date)));
          // Ett senare tillfälle i en serie (Spår R): öppna tillfällets egen
          // bokning (eller det virtuella tillfället), inte seriens första
          if (appointment.recurrence_pattern && appointment.recurrence_pattern !== 'none'
              && !String(appointment.start_time).startsWith(dateStr)) {
            try {
              appointment = await getOccurrence(groupId, dateStr);
            } catch (err) {
              console.error('Kunde inte slå upp tillfället:', err);
            }
          }
        }
        setGroupAppointment(appointment);
        setShowGroupModal(true);
      } catch (err) {
        console.error('Kunde inte öppna gruppbokning från startsidan:', err);
      } finally {
        // Rensa state så modalen inte återöppnas vid refresh/tillbaka
        navigate(location.pathname, { replace: true });
      }
    };

    openFromState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.openGroupAppointmentId]);

  // Hämta klinik-inställningar för journalmodul
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const resp = await apiClient.get('/api/journal/clinic/settings');
        setUseInternalJournal(resp.data.use_internal_journal || false);
      } catch (error) {
        setUseInternalJournal(false);
      }
    };

    fetchClinicSettings();
  }, []);

  // Ladda bokningar och blocks
  const loadData = useCallback(async () => {
    // Vänta tills en behandlare är vald innan vi hämtar något. Vid färsk
    // montering (t.ex. återgång från journal) är selectedUserId '' en kort
    // stund innan effekten sätter inloggad användare. Skulle vi hämta då blir
    // userFilter=null → backend returnerar ALLA behandlares bokningar och
    // veckovyn blir rörig (många smala lanes). Håll "Laddar" tills filtret satt.
    if (!selectedUserId) {
      setLoading(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const weekEnd = new Date(currentWeek);
      weekEnd.setDate(weekEnd.getDate() + 7);

      const userFilter = parseInt(selectedUserId);

      // Formatera datum for API (använd lokal tid, inte UTC)
      const fromDateStr = formatLocalDate(currentWeek);
      const toDateStr = formatLocalDate(weekEnd);

      const promises = [
        getAppointments(currentWeek.toISOString(), weekEnd.toISOString(), userFilter),
        getAppointmentTypes(),
        getBlocks(currentWeek.toISOString(), weekEnd.toISOString(), userFilter)
      ];

      // Hamta schemablock + schemamall-slots endast om en behandlare ar vald
      let slotsIndex = -1;
      if (userFilter) {
        promises.push(getScheduleBlocks(userFilter, fromDateStr, toDateStr));  // index 3
        slotsIndex = promises.length;  // index 4
        promises.push(getEffectiveSlots(userFilter, { from: fromDateStr, to: toDateStr }));
      }

      const results = await Promise.all(promises);

      setAppointments(results[0]);
      setAppointmentTypes(results[1]);
      setBlocks(results[2]);

      // Satt schemablock om data finns
      if (results[3]?.schedule_blocks) {
        setScheduleBlocks(results[3].schedule_blocks);
      } else {
        setScheduleBlocks([]);
      }

      // Satt schemamall-slots (nyckel: datum) om data finns
      if (slotsIndex >= 0 && results[slotsIndex]?.days) {
        setScheduleSlots(results[slotsIndex].days);
      } else {
        setScheduleSlots({});
      }
    } catch (err) {
      console.error('Error loading calendar data:', err);
      setError('Kunde inte ladda kalenderdata');
    } finally {
      setLoading(false);
    }
  }, [currentWeek, selectedUserId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Navigation
  const goToPreviousWeek = () => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() - 7);
    setCurrentWeek(newWeek);
  };

  const goToNextWeek = () => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + 7);
    setCurrentWeek(newWeek);
  };

  const goToToday = () => {
    setCurrentWeek(getWeekStart(new Date()));
  };

  // ===== DRAG-AND-DROP FUNKTIONER =====
  // Flytt (drag i kortet) och ändrad längd (drag i nederkanten, Spår K3).
  // Båda snappar i 5-minuterssteg (DRAG_SNAP_MINUTES, Spår S1) oavsett vald
  // upplösning, och flytt bevarar längden exakt.

  // Kontrollera om en bokning kan dras
  const canDragAppointment = useCallback((apt) => {
    // Betalda eller genomförda bokningar får inte dras
    return apt.status !== 'completed' && !apt.is_paid;
  }, []);

  // Snappa en tidpunkt till närmaste 5-minuterssteg (minuter på dygnet)
  const snapToGrid = useCallback((date) => {
    const totalMinutes = date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
    const snapped = Math.round(totalMinutes / DRAG_SNAP_MINUTES) * DRAG_SNAP_MINUTES;
    const result = new Date(date);
    result.setHours(Math.floor(snapped / 60), snapped % 60, 0, 0);
    return result;
  }, []);

  // Beräkna ny tid baserat på Y-offset - snappar den nya STARTTIDEN
  const calculateNewTime = useCallback((originalTime, deltaY) => {
    const deltaMinutes = deltaY / PIXELS_PER_MINUTE;
    return snapToGrid(new Date(new Date(originalTime).getTime() + deltaMinutes * 60 * 1000));
  }, [snapToGrid]);

  // Ny sluttid vid ändrad längd: snappad, och alltid efter starten
  const calculateResizedEnd = useCallback((item, deltaY) => {
    const start = new Date(item.start_time);
    const end = new Date(item.end_time);
    let newEnd = snapToGrid(new Date(end.getTime() + (deltaY / PIXELS_PER_MINUTE) * 60000));
    if (newEnd <= start) newEnd = new Date(start.getTime() + DRAG_SNAP_MINUTES * 60000);
    return newEnd;
  }, [snapToGrid]);

  // Ny starttid (tid på dygnet) vid flytt - samma regel som när draget
  // släpps: ett rent horisontellt drag (bara dagbyte) behåller exakt tid
  const calculateMovedStart = useCallback((startIso, deltaY) => (Math.abs(deltaY) < 5
    ? new Date(startIso)
    : calculateNewTime(startIso, deltaY)), [calculateNewTime]);

  // Formatera datum för API
  const formatDateTimeForApi = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:00`;
  }, []);

  // Starta drag. mode 'resize' = greppet i kortets nederkant (ändra längd).
  const handleDragStart = useCallback((e, itemType, item, dayIndex, mode = 'move') => {
    // Ignorera högerklick
    if (e.button !== 0) return;

    // Kontrollera om bokning kan dras
    if (itemType === 'appointment' && !canDragAppointment(item)) {
      return;
    }

    // Även greppet ligger i kortet - stoppa så kortets mousedown inte tar över
    e.stopPropagation();

    // Beräkna element-offset
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;

    dragRef.current = {
      startY: e.clientY,
      startX: e.clientX,
      offsetY: offsetY,
      mode,
      hasMoved: false
    };

    setDragState({
      isDragging: false, // Sätts till true efter initial rörelse
      itemType,
      // Återkommande block delar seriens block_id - nyckla per tillfälle
      itemId: itemType === 'appointment' ? appointmentKey(item) : blockKey(item),
      itemData: item,
      mode,
      startY: e.clientY,
      currentY: e.clientY,
      startX: e.clientX,
      currentX: e.clientX,
      originalTop: parseFloat(e.currentTarget.style.top) || 0,
      dayIndex
    });
  }, [canDragAppointment]);

  // Hantera drag-rörelse
  const handleDragMove = useCallback((e) => {
    if (!dragRef.current) return;

    // Starta drag på rörelse i valfri riktning - horisontellt drag byter dag
    // (Spår 7), vertikalt byter tid. (Enbart deltaY missade rena dagbyten.)
    // Längdgreppet reagerar bara vertikalt, och tidigare (smalt grepp).
    const deltaY = Math.abs(e.clientY - dragRef.current.startY);
    const deltaX = Math.abs(e.clientX - dragRef.current.startX);
    const isResize = dragRef.current.mode === 'resize';

    // Initial threshold för att skilja klick från drag
    if (!dragRef.current.hasMoved && (isResize ? deltaY < 3 : Math.max(deltaX, deltaY) < 10)) {
      return;
    }

    // Sätt hasMoved till true när vi passerat threshold
    if (!dragRef.current.hasMoved) {
      dragRef.current.hasMoved = true;
      document.body.style.cursor = isResize ? 'ns-resize' : '';
      document.body.style.userSelect = 'none';
      setDragState(prev => ({ ...prev, isDragging: true }));
    }

    setDragState(prev => ({
      ...prev,
      currentY: e.clientY,
      currentX: e.clientX
    }));
  }, []);

  // Spara ny tid för ett block. Ett återkommande block är EN rad i databasen
  // (tillfällena är virtuella) - ändringen gäller hela serien och översätts
  // därför till seriens egen start. Returnerar false om användaren avbröt.
  const saveBlockTimes = useCallback(async (block, newStart, newEnd) => {
    const isRecurringBlock = block.recurrence_pattern !== 'none' || block.is_recurring_instance;
    if (isRecurringBlock && !window.confirm('Detta är en återkommande post. Ändringen gäller hela serien. Fortsätt?')) {
      return false;
    }
    let start = newStart;
    if (block.is_recurring_instance && block.series_start_time) {
      start = new Date(new Date(block.series_start_time).getTime()
        + (newStart.getTime() - new Date(block.start_time).getTime()));
    }
    const end = new Date(start.getTime() + (newEnd.getTime() - newStart.getTime()));
    await updateBlock(block.block_id, {
      start_time: formatDateTimeForApi(start),
      end_time: formatDateTimeForApi(end)
    });
    return true;
  }, [formatDateTimeForApi]);

  // Ändra längd på bokning eller block (Spår K3). Returnerar true om något sparades.
  const finishResize = useCallback(async (itemType, itemData, deltaY) => {
    const newEnd = calculateResizedEnd(itemData, deltaY);
    if (newEnd.getTime() === new Date(itemData.end_time).getTime()) return false;

    if (itemType === 'appointment') {
      // Seriens första tillfälle (modern) bär hela serien (Spår R)
      if (isSeriesAppointment(itemData) && !itemData.parent_id && !isVirtualOccurrence(itemData)
          && !window.confirm('Detta är första tillfället i en återkommande serie. Ändra längden för hela serien?')) {
        return false;
      }
      // Ett virtuellt tillfälle materialiseras med sin nya längd (samma anrop
      // som flytt - krockkollen görs innan något skapas)
      const doPut = (extra = {}) => (isVirtualOccurrence(itemData)
        ? moveOccurrence(itemData, {
          start_time: formatDateTimeForApi(new Date(itemData.start_time)),
          end_time: formatDateTimeForApi(newEnd),
          ...extra
        })
        : updateAppointment(itemData.appointment_id, { end_time: formatDateTimeForApi(newEnd), ...extra }));
      try {
        await doPut();
      } catch (err) {
        if (!err.response?.data?.requires_conflict_override) throw err;
        const msg = err.response.data.error || 'Tiden krockar med en annan bokning.';
        if (!window.confirm(`${msg}\n\nVill du dubbelboka ändå?`)) return false;
        await doPut({ conflict_override: true });
      }
      return true;
    }
    return saveBlockTimes(itemData, new Date(itemData.start_time), newEnd);
  }, [calculateResizedEnd, formatDateTimeForApi, saveBlockTimes]);

  // Avsluta drag
  const handleDragEnd = useCallback(async (e) => {
    if (!dragRef.current) return;

    const wasRealDrag = dragRef.current.hasMoved;
    const state = dragState;

    dragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    setDragState({
      isDragging: false,
      itemType: null,
      itemId: null,
      itemData: null,
      mode: 'move',
      startY: 0,
      currentY: 0,
      originalTop: 0,
      dayIndex: null
    });

    if (!wasRealDrag) return;

    justDraggedRef.current = true;
    setTimeout(() => {
      justDraggedRef.current = false;
    }, 100);

    // Samma Y som förhandsvisningen (senaste mousemove), så det som sparas är
    // exakt den tid som visades på kortet (Spår S1)
    const deltaY = state.currentY - state.startY;

    if (state.mode === 'resize') {
      try {
        if (state.itemData && await finishResize(state.itemType, state.itemData, deltaY)) {
          loadData();
        }
      } catch (error) {
        console.error('Fel vid ändrad längd:', error);
        alert(error.response?.data?.error || 'Kunde inte ändra längden');
      }
      return;
    }

    // Spår 7: mappa släpp-X till dagkolumn (drag till annan dag i veckovyn).
    // Faller tillbaka på källdagen om släppet hamnar utanför en kolumn.
    const weekDatesNow = getWeekDates();
    const dropEl = document.elementFromPoint(e.clientX, e.clientY);
    const targetCol = dropEl?.closest('.day-column');
    let targetDayIndex = state.dayIndex;
    if (targetCol && targetCol.dataset.dayIndex != null) {
      targetDayIndex = Number(targetCol.dataset.dayIndex);
    }
    const dayChanged = targetDayIndex !== state.dayIndex;
    const targetDate = weekDatesNow[targetDayIndex] || weekDatesNow[state.dayIndex];

    // Varken tid- eller dagändring → ingen flytt. Samma beräkning som
    // förhandsvisningen: rent horisontellt drag behåller exakt tid.
    if (!state.itemData || (!dayChanged
        && calculateMovedStart(state.itemData.start_time, deltaY).getTime()
          === new Date(state.itemData.start_time).getTime())) return;

    // Behåll tid-på-dygnet men byt datumdel till måldagen
    const withDate = (dt, base) => {
      const d = new Date(dt);
      if (base) d.setFullYear(base.getFullYear(), base.getMonth(), base.getDate());
      return d;
    };
    // Rent horisontellt drag (bara dagbyte) → behåll exakt tid, snappa inte
    const baseStartFor = (startIso) => calculateMovedStart(startIso, deltaY);

    try {
      if (state.itemType === 'appointment' && state.itemData) {
        const durationMs = new Date(state.itemData.end_time || state.itemData.start_time).getTime()
                         - new Date(state.itemData.start_time).getTime();
        const newStart = withDate(baseStartFor(state.itemData.start_time), targetDate);
        const newEnd = new Date(newStart.getTime() + (durationMs > 0 ? durationMs : 30 * 60000));

        // Seriens första tillfälle (modern) bär hela serien - flytten gäller
        // alla tillfällen utan egna uppgifter (Spår R)
        if (isSeriesAppointment(state.itemData) && !state.itemData.parent_id && !isVirtualOccurrence(state.itemData)
            && !window.confirm('Detta är första tillfället i en återkommande serie. Flytta hela serien?')) {
          return; // bokningen snappar tillbaka
        }

        // Ett virtuellt tillfälle flyttas som eget tillfälle: skapas och flyttas
        // i samma anrop, efter krockkollen - ett avbrutet drag lämnar inget
        const times = {
          start_time: formatDateTimeForApi(newStart),
          end_time: formatDateTimeForApi(newEnd)
        };
        const doPut = (extra = {}) => (isVirtualOccurrence(state.itemData)
          ? moveOccurrence(state.itemData, { ...times, ...extra })
          : updateAppointment(state.itemData.appointment_id, { ...times, ...extra }));

        try {
          await doPut();
        } catch (err) {
          // Kollisionsfråga (I2): backend nekar krock med 409 → fråga dubbelboka
          if (err.response?.data?.requires_conflict_override) {
            const msg = err.response.data.error || 'Tiden krockar med en annan bokning.';
            if (window.confirm(`${msg}\n\nVill du dubbelboka ändå?`)) {
              await doPut({ conflict_override: true });
            } else {
              return; // avbröt → bokningen snappar tillbaka (ingen ändring sparad)
            }
          } else {
            throw err;
          }
        }
      } else if (state.itemType === 'block' && state.itemData) {
        // Längden bevaras exakt - bara starten snappar (Spår K3)
        const durationMs = new Date(state.itemData.end_time).getTime()
                         - new Date(state.itemData.start_time).getTime();
        const newStart = withDate(baseStartFor(state.itemData.start_time), targetDate);
        const newEnd = new Date(newStart.getTime() + (durationMs > 0 ? durationMs : 60 * 60000));

        if (!(await saveBlockTimes(state.itemData, newStart, newEnd))) return;
      }

      // Ladda om data
      loadData();
    } catch (error) {
      console.error('Error updating after drag:', error);
      alert(error.response?.data?.error || 'Kunde inte flytta bokningen');
    }
  }, [dragState, calculateMovedStart, formatDateTimeForApi, loadData, getWeekDates, finishResize, saveBlockTimes]);

  // Förhandsvisning av pågående drag för ett kort: { mode, start, end } i
  // minuter på dygnet - exakt de tider som sparas vid släpp (Spår S1) - annars null
  const getDragPreview = (itemId, itemType, item) => {
    if (!dragState.isDragging) return null;
    if (dragState.itemType !== itemType || dragState.itemId !== itemId) return null;
    const deltaY = dragState.currentY - dragState.startY;
    const toMinutes = (d) => d.getHours() * 60 + d.getMinutes();
    const origStart = new Date(item.start_time);
    if (dragState.mode === 'resize') {
      return { mode: 'resize', start: toMinutes(origStart), end: toMinutes(calculateResizedEnd(item, deltaY)) };
    }
    const newStart = calculateMovedStart(item.start_time, deltaY);
    const durationMs = new Date(item.end_time || item.start_time).getTime() - origStart.getTime();
    const fallbackMs = itemType === 'block' ? 60 * 60000 : 30 * 60000;
    const newEnd = new Date(newStart.getTime() + (durationMs > 0 ? durationMs : fallbackMs));
    return {
      mode: 'move',
      start: toMinutes(newStart),
      end: toMinutes(newEnd),
      shiftMinutes: (newStart.getTime() - origStart.getTime()) / 60000
    };
  };

  // Vertikal offset under flytt: kortet hoppar i 5-minuterssteg till den tid
  // det hamnar på (vid ändrad längd ligger kortet kvar och växer/krymper)
  const getDragOffset = (preview) => (preview?.mode === 'move'
    ? preview.shiftMinutes * PIXELS_PER_MINUTE
    : 0);

  // Resultattiden som visas på kortet under drag, t.ex. "10:35–11:05"
  const dragPreviewLabel = (preview) => (preview
    ? `${formatMinutes(preview.start)}–${formatMinutes(preview.end)}`
    : null);

  // useEffect för global mousemove/mouseup under drag
  useEffect(() => {
    if (!dragRef.current && !dragState.itemId) return;

    const handleMouseMove = (e) => handleDragMove(e);
    const handleMouseUp = (e) => handleDragEnd(e);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState.itemId, handleDragMove, handleDragEnd]);

  // ===== SLUT PÅ DRAG-AND-DROP FUNKTIONER =====

  // Klick på tom tid - avmarkera markerad bokning
  const handleSlotClick = (date, slot) => {
    setSelectedAppointmentId(null);
  };

  // Enkelklick på bokning - endast markera
  const handleAppointmentClick = (appointment, e) => {
    e.stopPropagation();
    // Toggle selection - klicka igen för att avmarkera
    const key = appointmentKey(appointment);
    if (selectedAppointmentId === key) {
      setSelectedAppointmentId(null);
    } else {
      setSelectedAppointmentId(key);
    }
  };

  // Dubbelklick på bokning - öppna modal
  const handleAppointmentDoubleClick = (appointment, e) => {
    e.stopPropagation();
    setSelectedAppointment(appointment);
    setSelectedSlot(null);
    setShowModal(true);
  };

  // Dubbelklick på tom tid - öppna ny bokning.
  // Träffar tiden en schemaslot förifylls slotens typ + tid, som vid högerklick.
  const handleSlotDoubleClick = (date, slot) => {
    if (selectedCalendarDisabled) return; // kalender avstängd - inga nya bokningar
    const scheduleSlot = getSlotAtTime(date, slot.hour, slot.minute);
    const slotDate = new Date(date);
    if (scheduleSlot) {
      const [sh, sm] = scheduleSlot.start_time.split(':').map(Number);
      const [eh, em] = scheduleSlot.end_time.split(':').map(Number);
      slotDate.setHours(sh, sm, 0, 0);
      setPreSelectedSlotType({
        type_id: scheduleSlot.appointment_type_id,
        name: scheduleSlot.appointment_type?.name,
        duration_minutes: (eh * 60 + em) - (sh * 60 + sm)
      });
    } else {
      slotDate.setHours(slot.hour, slot.minute, 0, 0);
    }
    setSelectedSlot(slotDate);
    setSelectedAppointment(null);
    setShowModal(true);
  };

  // Hitta schemaslot som täcker en given tid (för högerklick).
  // Vanlig funktion (ej useCallback) - getSlotsForDay deklareras längre ner,
  // och detta anropas endast i event-handlers (efter render).
  const getSlotAtTime = (date, hour, minute) => {
    const clickMinutes = hour * 60 + minute;
    return getSlotsForDay(date).find(s => {
      const [sh, sm] = s.start_time.split(':').map(Number);
      const [eh, em] = s.end_time.split(':').map(Number);
      return clickMinutes >= sh * 60 + sm && clickMinutes < eh * 60 + em;
    }) || null;
  };

  // Högerklick på tom tid - använder slotens exakta tid
  const handleSlotContextMenu = (date, slot, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedCalendarDisabled) return; // kalender avstängd - inga nya bokningar

    const slotDate = new Date(date);
    slotDate.setHours(slot.hour, slot.minute, 0, 0);

    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      appointment: null,
      block: null,
      slotDate: slotDate,
      scheduleSlot: getSlotAtTime(date, slot.hour, slot.minute),
      scheduleDate: date
    });
  };

  // Högerklick på bokning - visa kontextmeny
  const handleAppointmentContextMenu = (appointment, e) => {
    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      appointment: appointment,
      block: null,
      slotDate: new Date(appointment.start_time) // Använd bokningens starttid
    });
  };

  // Högerklick på block/note - visa kontextmeny
  const handleBlockContextMenu = (block, e) => {
    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      appointment: null,
      block: block,
      slotDate: new Date(block.start_time)
    });
  };

  // Stäng kontextmeny
  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  }, []);

  // Stäng kontextmeny vid klick utanför
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        closeContextMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('contextmenu', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside);
    };
  }, [contextMenu.visible, closeContextMenu]);

  // Kontextmeny-åtgärder
  const handleContextMenuAction = (action) => {
    closeContextMenu();

    switch (action) {
      case 'book':
        // Öppna bokningsmodal med vald tid
        setSelectedSlot(contextMenu.slotDate);
        setSelectedAppointment(null);
        setShowModal(true);
        break;

      case 'block':
        // Öppna block-modal
        setSelectedSlot(contextMenu.slotDate);
        setBlockModalMode('block');
        setSelectedBlock(null);
        setShowBlockModal(true);
        break;

      case 'note':
        // Öppna note-modal
        setSelectedSlot(contextMenu.slotDate);
        setBlockModalMode('note');
        setSelectedBlock(null);
        setShowBlockModal(true);
        break;

      case 'open':
        // Öppna befintlig bokning
        setSelectedAppointment(contextMenu.appointment);
        setSelectedSlot(null);
        setShowModal(true);
        break;

      case 'cancel':
        // Avboka tid
        if (contextMenu.appointment) {
          handleCancelAppointment(contextMenu.appointment);
        }
        break;

      case 'endSeries':
        // Avsluta återkommande serie från ett datum (bokningar raderas aldrig, K1)
        if (contextMenu.appointment) {
          handleEndSeries(contextMenu.appointment);
        }
        break;

      case 'copy':
        // Kopiera bokning (Spår K4) - klistras in via högerklick på tom tid
        if (contextMenu.appointment) {
          copyAppointment(contextMenu.appointment);
        }
        break;

      case 'paste':
        handlePasteAppointment(contextMenu.slotDate);
        break;

      case 'reschedule':
        // Omboka tid - öppna modal för redigering
        setSelectedAppointment(contextMenu.appointment);
        setSelectedSlot(null);
        setShowModal(true);
        break;

      case 'bookNew':
        // Boka ny tid för bokningens patient: gör patienten aktiv och öppna
        // "Hitta ledig tid" - modalen förväljer den aktiva patienten
        if (contextMenu.appointment?.patient) {
          setActivePatient?.(contextMenu.appointment.patient);
          setShowSlotSearch(true);
        }
        break;

      case 'payment':
        // Öppna betalningsmodal (is_group -> deltagarlistan via handleOpenPaymentModal)
        if (contextMenu.appointment) {
          handleOpenPaymentModal(contextMenu.appointment);
        }
        break;

      case 'noShow':
        // Registrera uteblivet: uteblivet-avgift i kassan + röd cell +
        // fakturaunderlag mailas automatiskt (Spår D+E)
        if (contextMenu.appointment) {
          handleRegisterNoShow(contextMenu.appointment);
        }
        break;

      case 'openBlock':
        // Öppna block/note för redigering
        if (contextMenu.block) {
          setSelectedBlock(contextMenu.block);
          // Sick och vacation är block-typer, så använd 'block' mode
          setBlockModalMode(contextMenu.block.block_type === 'note' ? 'note' : 'block');
          setSelectedSlot(new Date(contextMenu.block.start_time));
          setShowBlockModal(true);
        }
        break;

      case 'deleteBlock':
        // Ta bort block/note
        if (contextMenu.block) {
          handleDeleteBlock(contextMenu.block);
        }
        break;

      case 'openJournal':
        // Navigera till patientens journal-flik
        if (contextMenu.appointment?.patient) {
          navigate(`/patients/${contextMenu.appointment.patient.patient_id}?tab=journal`);
        }
        break;

      case 'newJournal':
      case 'startRecording':
        // Båda öppnar patientens journalflik med "Ny journal"-modalen
        // förkopplad till bokningen. JournalTab äger skapandet och öppnar
        // den nya anteckningen i redigering (+ startar dikteringen om
        // "Starta inspelning" valdes) — kalendern har ingen egen modal.
        if (contextMenu.appointment?.patient) {
          const p = contextMenu.appointment.patient;
          // Ett virtuellt tillfälle skickas som referens "<serie>@<datum>" och
          // skapas först när anteckningen sparas (NewJournalModal, Spår R)
          const ref = encodeURIComponent(appointmentKey(contextMenu.appointment));
          navigate(`/patients/${p.patient_id}?tab=journal&ny_journal=1&appointment_id=${ref}`);
        }
        break;

      case 'manageParticipants':
        // Hantera gruppdeltagare
        if (contextMenu.appointment?.is_group) {
          handleOpenGroupParticipants(contextMenu.appointment);
        }
        break;

      case 'bookSlot': {
        // Boka på schemaslot - förifyll typ + slotens längd
        const s = contextMenu.scheduleSlot;
        if (s) {
          const [sh, sm] = s.start_time.split(':').map(Number);
          const [eh, em] = s.end_time.split(':').map(Number);
          const slotLen = (eh * 60 + em) - (sh * 60 + sm);
          const slotDate = new Date(contextMenu.scheduleDate);
          slotDate.setHours(sh, sm, 0, 0);
          setPreSelectedSlotType({
            type_id: s.appointment_type_id,
            name: s.appointment_type?.name,
            duration_minutes: slotLen
          });
          setSelectedSlot(slotDate);
          setSelectedAppointment(null);
          setShowModal(true);
        }
        break;
      }

      case 'addSlot': {
        // Lägg en ny schemaslot denna dag — förifyll den klickade tiden (Spår C).
        // scheduleDate är datum-only; slotDate bär den klickade timmen/minuten.
        const clicked = contextMenu.slotDate;
        setSlotModal({
          mode: 'add',
          date: contextMenu.scheduleDate || clicked,
          slot: null,
          defaultStartTime: clicked
            ? `${String(clicked.getHours()).padStart(2, '0')}:${String(clicked.getMinutes()).padStart(2, '0')}`
            : null
        });
        break;
      }

      case 'editSlot':
        // Ändra befintlig schemaslot denna dag
        if (contextMenu.scheduleSlot) {
          setSlotModal({ mode: 'edit', date: contextMenu.scheduleDate, slot: contextMenu.scheduleSlot });
        }
        break;

      case 'removeSlot':
        // Ta bort schemaslot denna dag (mall-slot -> override 'remove',
        // add-avvikelse -> radera avvikelsen)
        if (contextMenu.scheduleSlot) {
          handleRemoveSlot(contextMenu.scheduleSlot, contextMenu.scheduleDate);
        }
        break;

      default:
        break;
    }
  };

  // Ta bort en schemaslot för en specifik dag
  const handleRemoveSlot = async (slot, date) => {
    if (!window.confirm('Ta bort schemasloten för denna dag? Mallen påverkas inte.')) return;
    try {
      if (slot.slot_id) {
        const dateStr = formatLocalDate(date);
        await createSlotOverride({
          user_id: parseInt(selectedUserId), date: dateStr,
          action: 'remove', source_slot_id: slot.slot_id
        });
      } else if (slot.override_id) {
        await deleteSlotOverride(slot.override_id);
      }
      loadData();
    } catch (err) {
      setError('Kunde inte ta bort schemasloten');
    }
  };

  // Efter att slot-override sparats
  const handleSlotModalSaved = () => {
    setSlotModal(null);
    loadData();
  };

  // Öppna betalningsmodal
  const handleOpenPaymentModal = (appointment) => {
    // Gruppbokningar har ingen egen patient - betalning sker per deltagare.
    // Skicka vidare till deltagarlistan i stället för individmodalen (annars
    // öppnas betalning för "Ingen patient"). Gäller båda ingångarna:
    // högerklicksmenyn och "Registrera betalning"-knappen i bokningen.
    if (appointment?.is_group) {
      handleOpenGroupParticipants(appointment);
      return;
    }
    setPaymentAppointment(appointment);
    setShowPaymentModal(true);
  };

  // Stäng betalningsmodal
  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentAppointment(null);
  };

  // Efter betalning sparats
  const handlePaymentSaved = () => {
    handleClosePaymentModal();
    loadData();
  };

  // Registrera uteblivet besök (Spår D+E)
  const handleRegisterNoShow = async (appointment) => {
    if (!window.confirm(await uteblivetBekraftelseText('Registrera bokningen som uteblivet?', appointment.patient_id, appointment.start_time))) return;
    try {
      const real = await ensureRealAppointment(appointment);
      const res = await registerNoShow(real.appointment_id);
      if (res.fakturaunderlag_sent === false) {
        alert(`Uteblivet registrerat, men fakturaunderlaget kunde inte mailas: ${res.fakturaunderlag_error || 'okänt fel'}`);
      }
      loadData();
    } catch (err) {
      console.error('Kunde inte registrera uteblivet:', err);
      alert(err.response?.data?.error || 'Kunde inte registrera uteblivet');
    }
  };

  // Kallelse modal handlers
  // Ett virtuellt tillfälle skapas först när kallelsen skickas (KallelseModal)
  const handleOpenKallelseModal = (appointment, preSelectedTemplate = null) => {
    setKallelseAppointment(appointment);
    setPreSelectedKallelseTemplate(preSelectedTemplate);
    setShowKallelseModal(true);
  };

  const handleCloseKallelseModal = () => {
    setShowKallelseModal(false);
    setKallelseAppointment(null);
    setPreSelectedKallelseTemplate(null);
  };

  // Block modal handlers
  const handleCloseBlockModal = () => {
    setShowBlockModal(false);
    setSelectedBlock(null);
  };

  const handleBlockSaved = () => {
    handleCloseBlockModal();
    loadData();
  };

  // Group participants modal handlers
  const handleOpenGroupParticipants = (appointment) => {
    setGroupAppointment(appointment);
    setShowGroupModal(true);
  };

  const handleCloseGroupModal = () => {
    setShowGroupModal(false);
    setGroupAppointment(null);
  };

  const handleGroupUpdated = () => {
    loadData();
  };

  // Slot search handlers
  const handleSlotSelected = (slotData) => {
    setShowSlotSearch(false);

    // Navigera till ratt vecka
    const slotDate = new Date(slotData.start_time);
    setCurrentWeek(getWeekStart(slotDate));

    // Oppna bokningsmodal med forvalda varden (tid, behandlare, typ + langd)
    setPreSelectedSlotType({
      type_id: slotData.appointment_type_id,
      name: slotData.type_name,
      duration_minutes: slotData.duration_minutes
    });
    setSelectedSlot(slotDate);
    setSelectedAppointment(null);
    setSelectedUserId(String(slotData.user_id));
    setShowModal(true);
  };

  // Ta bort block/note från kontextmeny
  const handleDeleteBlock = async (block) => {
    const hasRecurrence = block.recurrence_pattern !== 'none' || block.is_recurring_instance;

    // Textbeskrivning baserat på block-typ
    const typeLabels = {
      block: 'blockering',
      sick: 'sjukdom',
      vacation: 'semester',
      note: 'anteckning'
    };
    const typeLabel = typeLabels[block.block_type] || 'blockering';

    let deleteSeries = false;

    if (hasRecurrence) {
      const choice = window.confirm(
        `Vill du ta bort hela serien? (OK = Ta bort alla, Avbryt = Ta bara bort denna ${typeLabel})`
      );
      deleteSeries = choice;
    } else {
      if (!window.confirm(`Är du säker på att du vill ta bort denna ${typeLabel}?`)) {
        return;
      }
    }

    try {
      await deleteBlock(block.block_id, deleteSeries);
      loadData();
    } catch (err) {
      console.error('Error deleting block:', err);
      alert('Kunde inte ta bort');
    }
  };

  // Klick på block/note
  const handleBlockClick = (block, e) => {
    e.stopPropagation();
    setSelectedBlock(block);
    // Sick och vacation är block-typer, så använd 'block' mode
    // BlockNoteModal läser block_type från existingBlock och sätter rätt kategori
    setBlockModalMode(block.block_type === 'note' ? 'note' : 'block');
    setSelectedSlot(new Date(block.start_time));
    setShowBlockModal(true);
  };

  // Stäng modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAppointment(null);
    setSelectedSlot(null);
    setPreSelectedSlotType(null);
  };

  // Spara bokning (skapa/uppdatera)
  const handleSaveAppointment = () => {
    handleCloseModal();
    loadData();
  };

  // Bokningar tas aldrig bort (Spår K1). En återkommande serie avslutas i
  // stället från och med ett datum: senare tillfällen avbokas/genereras
  // inte, tidigare ligger kvar.
  const handleEndSeries = async (appointment) => {
    // Förslag: klickat tillfälle, men aldrig bakåt i tiden (historiken ligger kvar)
    const today = formatLocalDate(new Date());
    const occ = occurrenceDateOf(appointment);
    const suggested = occ > today ? occ : today;
    const answer = window.prompt(
      'Avsluta serien från och med vilket datum? (ÅÅÅÅ-MM-DD, i dag eller senare)\n\n'
      + 'Tillfällen från och med datumet avbokas och inga nya skapas. '
      + 'Tidigare tillfällen påverkas inte.',
      suggested
    );
    if (answer === null) return;
    const fromDate = answer.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fromDate)) {
      alert('Ange datumet som ÅÅÅÅ-MM-DD');
      return;
    }
    try {
      const res = await endSeries(appointment.appointment_id, fromDate);
      if (res.kept_occurrences) {
        alert(`Serien är avslutad. ${res.kept_occurrences} tillfälle(n) efter datumet är redan `
          + 'genomförda eller betalda och ligger kvar.');
      }
      loadData();
      handleCloseModal();
    } catch (err) {
      console.error('Kunde inte avsluta serien:', err);
      alert(err.response?.data?.error || 'Kunde inte avsluta serien');
    }
  };

  // Klistra in kopierad bokning på en tom tid (Spår K4) via skapa-flödet.
  // Behandlare = den som visas i veckovyn.
  const handlePasteAppointment = async (slotDate) => {
    if (!slotDate || !getCopiedAppointment() || !selectedUserId) return;
    try {
      const created = await pasteAppointment(slotDate, selectedUserId);
      if (created) loadData();
    } catch (err) {
      console.error('Kunde inte klistra in bokningen:', err);
      alert(err.response?.data?.error || 'Kunde inte klistra in bokningen');
    }
  };

  // Avboka - alltid bara detta tillfälle (Spår R: ett virtuellt tillfälle
  // materialiseras först så att inte seriens första tillfälle avbokas)
  const handleCancelAppointment = async (appointment) => {
    const question = isSeriesAppointment(appointment)
      ? 'Avboka detta tillfälle? Övriga tillfällen i serien påverkas inte.'
      : 'Är du säker på att du vill avboka denna tid?';
    if (window.confirm(question)) {
      try {
        const real = await ensureRealAppointment(appointment);
        await cancelAppointment(real.appointment_id);
        loadData();
        handleCloseModal();
      } catch (err) {
        console.error('Error cancelling appointment:', err);
        alert('Kunde inte avboka tiden');
      }
    }
  };

  // Hämta alla bokningar för en viss dag med positionsberäkning
  // Unified collision detection för alla events (bokningar + blocks) för en dag
  const getPositionedEventsForDay = useCallback((date) => {
    // Filtrera bokningar för denna dag
    const dayAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.start_time);
      return aptDate.toDateString() === date.toDateString();
    });

    // Filtrera blocks för denna dag
    const dayBlocks = blocks.filter(block => {
      const blockDate = new Date(block.start_time);
      return blockDate.toDateString() === date.toDateString();
    });

    // Konvertera bokningar till event-objekt
    const aptEvents = dayAppointments.map(apt => {
      const start = new Date(apt.start_time);
      const startMinutes = start.getHours() * 60 + start.getMinutes();

      let endMinutes;
      if (apt.end_time) {
        const end = new Date(apt.end_time);
        if (!isNaN(end.getTime())) {
          endMinutes = end.getHours() * 60 + end.getMinutes();
          if (endMinutes < startMinutes && end.getDate() !== start.getDate()) {
            endMinutes += 24 * 60;
          }
        }
      }

      if (!endMinutes || endMinutes <= startMinutes) {
        if (apt.appointment_type?.duration_minutes) {
          endMinutes = startMinutes + apt.appointment_type.duration_minutes;
        } else {
          endMinutes = startMinutes + 30;
        }
      }

      const maxMinutes = 19 * 60;
      if (endMinutes > maxMinutes) {
        endMinutes = maxMinutes;
      }

      return {
        ...apt,
        eventType: 'appointment',
        eventId: apt.appointment_id,
        startMinutes,
        endMinutes,
        column: 0,
        totalColumns: 1
      };
    });

    // Konvertera blocks till event-objekt
    const blockEvents = dayBlocks.map(block => {
      const start = new Date(block.start_time);
      const end = new Date(block.end_time);

      const startMinutes = start.getHours() * 60 + start.getMinutes();
      let endMinutes = end.getHours() * 60 + end.getMinutes();
      if (endMinutes <= startMinutes) {
        endMinutes = startMinutes + 60;
      }

      return {
        ...block,
        eventType: 'block',
        eventId: block.block_id,
        startMinutes,
        endMinutes,
        column: 0,
        totalColumns: 1
      };
    });

    // Avbokade bokningar tas UT ur kollisionslayouten och samlas till en liten
    // ikon per starttid (Spår S2) - aktiva bokningar behåller full bredd.
    const activeAptEvents = aptEvents.filter(e => e.status !== 'cancelled');
    const positionedCancelled = groupCancelledByStart(aptEvents.filter(e => e.status === 'cancelled'));

    // Kombinera aktiva bokningar + blocks för kollisionslayout
    const allEvents = [...activeAptEvents, ...blockEvents];

    if (allEvents.length === 0) {
      return { appointments: [], blocks: [], cancelled: positionedCancelled };
    }

    // Sortera efter starttid, längst först vid samma starttid
    allEvents.sort((a, b) => {
      if (a.startMinutes !== b.startMinutes) {
        return a.startMinutes - b.startMinutes;
      }
      return (b.endMinutes - b.startMinutes) - (a.endMinutes - a.startMinutes);
    });

    // Tilldela kolumner
    const columnEndTimes = [];

    allEvents.forEach(event => {
      let assignedColumn = -1;

      for (let col = 0; col < columnEndTimes.length; col++) {
        if (event.startMinutes >= columnEndTimes[col]) {
          assignedColumn = col;
          break;
        }
      }

      if (assignedColumn === -1) {
        assignedColumn = columnEndTimes.length;
        columnEndTimes.push(0);
      }

      event.column = assignedColumn;
      columnEndTimes[assignedColumn] = event.endMinutes;
    });

    // Beräkna totalColumns för varje event baserat på överlappande events
    allEvents.forEach(event => {
      const overlapping = allEvents.filter(other =>
        event.startMinutes < other.endMinutes && event.endMinutes > other.startMinutes
      );
      const maxColumn = Math.max(...overlapping.map(o => o.column));
      event.totalColumns = maxColumn + 1;
    });

    // Lämna plats åt avbokningsikonen i kolumnens högerkant (Spår S2)
    applyCancelledGutter(allEvents, positionedCancelled, PIXELS_PER_MINUTE);

    // Separera tillbaka till bokningar och blocks
    const positionedAppointments = allEvents.filter(e => e.eventType === 'appointment');
    const positionedBlocks = allEvents.filter(e => e.eventType === 'block');

    return { appointments: positionedAppointments, blocks: positionedBlocks, cancelled: positionedCancelled };
  }, [appointments, blocks]);

  // Wrapper-funktioner för bakåtkompatibilitet
  const getPositionedAppointmentsForDay = useCallback((date) => {
    return getPositionedEventsForDay(date).appointments;
  }, [getPositionedEventsForDay]);

  const getPositionedBlocksForDay = useCallback((date) => {
    return getPositionedEventsForDay(date).blocks;
  }, [getPositionedEventsForDay]);

  const getPositionedCancelledForDay = useCallback((date) => {
    return getPositionedEventsForDay(date).cancelled;
  }, [getPositionedEventsForDay]);

  // Hämta schema för en specifik dag
  const getScheduleForDay = useCallback((date) => {
    const dateStr = formatLocalDate(date);
    return scheduleBlocks.find(block => block.date === dateStr);
  }, [scheduleBlocks]);

  // Hämta schemamall-slots för en specifik dag
  const getSlotsForDay = useCallback((date) => {
    return scheduleSlots[formatLocalDate(date)] || [];
  }, [scheduleSlots]);

  const weekDates = getWeekDates();

  // Deltagarmodalen (gruppbokning) - renderas i båda vyerna så att
  // startsidans "öppna grupp" fungerar även när kalendern står i dagvy
  const groupParticipantsModal = showGroupModal && groupAppointment && (
    <GroupParticipantsModal
      appointment={groupAppointment}
      onClose={handleCloseGroupModal}
      onUpdate={handleGroupUpdated}
      onOpenPatient={(patient) => {
        handleCloseGroupModal();
        navigate(`/patients/${patient.patient_id}?tab=journal`);
      }}
    />
  );

  // Visa dagsvy om vald
  if (currentView === 'day') {
    return (
      <>
        <DayView onViewChange={setCurrentView} />
        {groupParticipantsModal}
      </>
    );
  }

  if (loading && appointments.length === 0) {
    return <div className="calendar-loading">Laddar kalender...</div>;
  }

  return (
    <div className="calendar-container">
      {/* Header */}
      <div className="calendar-header">
        <div className="calendar-header-top">
          <div className="calendar-nav">
            <button onClick={goToPreviousWeek} className="nav-btn" title="Föregående vecka">
              <i className="fas fa-chevron-left"></i> Förra veckan
            </button>
            <button onClick={goToToday} className="nav-btn today-btn">Idag</button>
            <button onClick={goToNextWeek} className="nav-btn" title="Nästa vecka">
              Nästa vecka <i className="fas fa-chevron-right"></i>
            </button>
            {/* Spår K2.1: hoppa till valfri vecka via månadskalender */}
            <DateJumpPicker
              selectedDate={currentWeek}
              onSelectDate={(date) => setCurrentWeek(getWeekStart(date))}
              highlightWeek
            />
          </div>
          <h2 className="calendar-title">
            {/* ISO-år: vecka 1 som börjar i december hör till nästa år */}
            Vecka {isoWeekNumber(currentWeek)} - {isoWeekYear(currentWeek)}
          </h2>
          <div className="header-buttons">
            <button
              className="view-toggle-btn active"
              title="Veckovy"
            >
              <i className="fas fa-calendar-week"></i>
              Vecka
            </button>
            <button
              className="view-toggle-btn"
              onClick={() => setCurrentView('day')}
              title="Dagsvy"
            >
              <i className="fas fa-calendar-day"></i>
              Dag
            </button>
            <button
              className="find-time-btn"
              onClick={() => setShowSlotSearch(true)}
              title="Hitta ledig tid"
            >
              <i className="fas fa-search"></i>
              Hitta tid
            </button>
            {/* "Schema" (arbetstider) nås numera via kalenderinställningar (endast admin) */}
            <button
              className="behandlare-sjuk-btn"
              onClick={() => setShowBehandlareSjukModal(true)}
              title="Behandlare sjuk - meddela patienter"
            >
              <i className="fas fa-user-injured"></i>
              Sjukanmälan
            </button>

            {/* Tidsintervall-väljare */}
            <div className="interval-selector">
              <i className="fas fa-th"></i>
              <select
                value={slotInterval}
                onChange={(e) => setSlotInterval(Number(e.target.value))}
                title="Välj tidsintervall för schemat"
              >
                <option value={60}>60 min</option>
                <option value={30}>30 min</option>
                <option value={15}>15 min</option>
                <option value={10}>10 min</option>
              </select>
            </div>
            <CalendarLegend />
          </div>
        </div>

        {/* Behandlar-filter */}
        <div className="calendar-filter">
          <span className="filter-label">
            <i className="fas fa-user-md"></i>
            Behandlare:
          </span>
          <div className="user-filter-buttons">
            {clinicUsers.map(user => (
              <button
                key={user.user_id}
                className={`user-filter-btn ${selectedUserId === String(user.user_id) ? 'active' : ''} ${user.has_calendar === false ? 'calendar-disabled' : ''}`}
                onClick={() => setSelectedUserId(String(user.user_id))}
                title={user.has_calendar === false ? 'Kalender avstängd - har bokningar denna vecka' : undefined}
              >
                {user.first_name} {user.last_name}
                {user.has_calendar === false && <span className="calendar-disabled-tag">Kalender avstängd</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <div className="calendar-error">{error}</div>}

      {selectedCalendarDisabled && (
        <div className="calendar-disabled-banner">
          <i className="fas fa-calendar-times"></i>
          Kalender avstängd — användaren tar inte emot nya bokningar. Befintliga
          bokningar kan avbokas, eller flyttas till en annan behandlare i dagvyn.
        </div>
      )}

      {/* Kalenderrutnät */}
      <div className={`calendar-grid ${selectedCalendarDisabled ? 'calendar-disabled' : ''}`} style={{ '--slot-height': `${slotHeight}px` }}>
        {/* Tidkolumn */}
        <div className="time-column">
          <div className="header-cell"></div>
          {timeSlots.map((slot) => (
            <div
              key={`${slot.hour}-${slot.minute}`}
              className={`time-cell ${slot.minute !== 0 ? 'sub-hour' : ''}`}
              style={{ height: `${slotHeight}px` }}
            >
              {formatSlotTime(slot)}
            </div>
          ))}
        </div>

        {/* Dagkolumner */}
        {weekDates.map((date, dayIndex) => {
          const positionedAppointments = getPositionedAppointmentsForDay(date);
          const positionedBlocks = getPositionedBlocksForDay(date);
          const positionedCancelled = getPositionedCancelledForDay(date);
          const daySchedule = getScheduleForDay(date);

          // Kontrollera om en slot är inom schema
          const isSlotInSchedule = (slot) => {
            if (!daySchedule || !daySchedule.has_schedule) return false;
            const [scheduleStartHour, scheduleStartMin] = daySchedule.start_time.split(':').map(Number);
            const [scheduleEndHour, scheduleEndMin] = daySchedule.end_time.split(':').map(Number);
            const scheduleStartMinutes = scheduleStartHour * 60 + scheduleStartMin;
            const scheduleEndMinutes = scheduleEndHour * 60 + scheduleEndMin;
            const slotMinutes = slot.hour * 60 + slot.minute;
            return slotMinutes >= scheduleStartMinutes && slotMinutes < scheduleEndMinutes;
          };

          const isDayToday = date.toDateString() === new Date().toDateString();

          return (
            <div key={dayIndex} className="day-column" data-day-index={dayIndex}>
              <div className={`header-cell day-header ${isDayToday ? 'today' : ''}`}>
                <span className="day-name">{DAYS[dayIndex]}</span>
                <span className="day-date">{date.getDate()}/{date.getMonth() + 1}</span>
              </div>
              <div className="day-slots-container">
                {/* Nu-linje (Spår 6): bara i dagens kolumn */}
                {isDayToday && nowVisible && (
                  <div className="now-line" style={{ top: `${nowTopPx}px` }}>
                    <span className="now-line-dot" />
                  </div>
                )}
                {/* Tids-slots för klickbar yta */}
                {timeSlots.map(slot => {
                  const inSchedule = isSlotInSchedule(slot);
                  const hasSchedule = daySchedule?.has_schedule;
                  const outsideSchedule = hasSchedule && !inSchedule;

                  return (
                    <div
                      key={`${slot.hour}-${slot.minute}`}
                      className={`time-slot ${outsideSchedule ? 'outside-schedule' : ''} ${!hasSchedule && selectedUserId ? 'no-schedule-day' : ''} ${slot.minute !== 0 ? 'sub-hour' : ''}`}
                      style={{ height: `${slotHeight}px` }}
                      onClick={() => handleSlotClick(date, slot)}
                      onDoubleClick={() => handleSlotDoubleClick(date, slot)}
                      onContextMenu={(e) => handleSlotContextMenu(date, slot, e)}
                      title={outsideSchedule ? 'Utanför arbetstid' : ((!hasSchedule && selectedUserId) ? 'Ingen arbetstid inlagd' : '')}
                    />
                  );
                })}

                {/* Schemamall-slots - tunn remsa till vänster (visuell indikator) */}
                {getSlotsForDay(date).map((s, i) => {
                  const [sh, sm] = s.start_time.split(':').map(Number);
                  const [eh, em] = s.end_time.split(':').map(Number);
                  const slotStart = sh * 60 + sm;
                  const slotEnd = eh * 60 + em;
                  const slotTop = (slotStart - START_HOUR * 60) * PIXELS_PER_MINUTE;
                  const slotHeightPx = Math.max((slotEnd - slotStart) * PIXELS_PER_MINUTE - 1, 8);
                  // Klipp bort slots utanför synligt tidsspann
                  if (slotEnd <= START_HOUR * 60 || slotStart >= END_HOUR * 60) return null;
                  const slotColor = s.appointment_type?.color || '#6c757d';
                  const typeName = s.appointment_type?.name || 'Schemaslot';
                  return (
                    <div
                      key={`slot-${dayIndex}-${i}`}
                      className="schedule-slot-strip"
                      style={{ top: `${slotTop}px`, height: `${slotHeightPx}px`, backgroundColor: slotColor }}
                      title={`${typeName} ${s.start_time}–${s.end_time}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSlotInfo({ x: e.clientX, y: e.clientY, slot: s });
                      }}
                      onContextMenu={(e) => handleSlotContextMenu(date, { hour: sh, minute: sm }, e)}
                    />
                  );
                })}

                {/* Bokningar renderade med absolut positionering */}
                {positionedAppointments.map(apt => {
                  // Beräkna position baserat på minuter × vertikal skala
                  const topOffset = (apt.startMinutes - START_HOUR * 60) * PIXELS_PER_MINUTE;
                  const aptKey = appointmentKey(apt);
                  // Under drag (Spår S1): kortet hoppar i 5-min-steg, och vid
                  // ändrad längd (Spår K3) växer det till den snappade sluttiden
                  const dragPreview = getDragPreview(aptKey, 'appointment', apt);
                  const resizeEnd = dragPreview?.mode === 'resize' ? dragPreview.end : null;
                  const durationMinutes = (resizeEnd ?? apt.endMinutes) - apt.startMinutes;
                  // Minst 20px höjd för synlighet
                  const height = Math.max(durationMinutes * PIXELS_PER_MINUTE - 2, 20);

                  // Beräkna bredd och horisontell position för överlappande
                  const colFraction = 1 / apt.totalColumns;
                  const width = `calc(100% * ${colFraction} - ${4 + (apt.cancelledGutter || 0)}px)`;
                  const left = `calc(100% * ${apt.column * colFraction} + 2px)`;

                  // Avgör om det är en kort bokning (under 45 min)
                  const isShort = durationMinutes < 45;

                  // Drag-relaterat
                  const isDraggable = canDragAppointment(apt);
                  const dragOffset = getDragOffset(dragPreview);
                  const isBeingDragged = dragState.isDragging && dragState.itemId === aptKey && dragState.itemType === 'appointment';
                  // Horisontell preview: kortet följer pekaren i sidled under drag
                  // så man ser att bokningen dras mot en annan dag (Spår 7).
                  const dragOffsetX = isBeingDragged && dragState.mode !== 'resize' ? (dragState.currentX - dragState.startX) : 0;
                  const isSelected = selectedAppointmentId === aptKey;

                  // Gruppdeltagare (aktiva, dvs ej avbokade) för cell + tooltip (G5)
                  const bookedBy = bookedByText(apt); // Spår K2.2
                  const activeParticipants = apt.is_group && Array.isArray(apt.participants)
                    ? apt.participants.filter(p => p.status !== 'cancelled')
                    : [];
                  const participantCount = activeParticipants.length;

                  // Läsbar textfärg utifrån bakgrunden (skarpare än vit på pastell).
                  // Uteblivna bokningar är röda oavsett typfärg (Spår D).
                  const bgColor = apt.status === 'no_show'
                    ? '#dc3545'
                    : (apt.appointment_type?.color || (apt.is_group ? '#6f42c1' : '#3788d8'));

                  return (
                    <div
                      key={aptKey}
                      className={`appointment appointment-positioned ${apt.status} ${isShort ? 'appointment-short' : ''} ${apt.is_paid ? 'paid' : ''} ${apt.is_group ? 'group-appointment' : ''} ${isDraggable ? 'draggable' : 'not-draggable'} ${isBeingDragged ? 'dragging' : ''} ${isSelected ? 'selected' : ''}`}
                      style={{
                        backgroundColor: bgColor,
                        color: textColorForBackground(bgColor),
                        top: `${topOffset + dragOffset}px`,
                        height: `${height}px`,
                        width: width,
                        left: left,
                        transform: isBeingDragged ? `translateX(${dragOffsetX}px) scale(1.02)` : undefined,
                        // Ingen transition under drag → kortet följer pekaren direkt
                        // i sidled (annars laggar transform: transform 0.1s efter).
                        transition: isBeingDragged ? 'none' : undefined,
                        // Under drag: släpp igenom pekaren så elementFromPoint
                        // hittar måldagens kolumn, inte det dragna kortet (Spår 7)
                        pointerEvents: isBeingDragged ? 'none' : undefined,
                        zIndex: isBeingDragged ? 1000 : undefined
                      }}
                      onClick={(e) => {
                        if (justDraggedRef.current) return;
                        handleAppointmentClick(apt, e);
                      }}
                      onDoubleClick={(e) => handleAppointmentDoubleClick(apt, e)}
                      onContextMenu={(e) => handleAppointmentContextMenu(apt, e)}
                      onMouseDown={(e) => handleDragStart(e, 'appointment', apt, dayIndex)}
                    >
                      {/* Låst bokning (Spår K5): förklaring i stället för en ren CSS-prick */}
                      {!isDraggable && (
                        <span
                          className="apt-lock-dot"
                          title={apt.is_paid ? 'Betald – kan inte flyttas' : 'Genomförd – kan inte flyttas'}
                          aria-label={apt.is_paid ? 'Betald – kan inte flyttas' : 'Genomförd – kan inte flyttas'}
                        />
                      )}
                      {dragPreview && (
                        <span className="drag-preview-label">{dragPreviewLabel(dragPreview)}</span>
                      )}
                      <div className="appointment-content">
                        {apt.is_paid && <span className="paid-indicator" title="Betald"><i className="fas fa-check-circle"></i></span>}
                        <span className={`appointment-patient${apt.is_group ? ' is-group-row' : ''}`}>
                          {apt.is_group && (
                            <i className="fas fa-users group-inline-icon" title="Gruppbokning"></i>
                          )}
                          <span className="appointment-name-text">
                            {apt.is_group ? apt.group_name : (apt.patient ? apt.patient.fullname : 'Ingen patient')}
                          </span>
                          {apt.is_group && (
                            <span className="group-participant-count" title={`${participantCount} bokade`}>
                              {participantCount}
                            </span>
                          )}
                        </span>
                        {!isShort && (
                          <span className="appointment-time">
                            {new Date(apt.start_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                            {apt.appointment_type && ` - ${apt.appointment_type.name}`}
                          </span>
                        )}
                      </div>
                      {/* Hover tooltip */}
                      <div className="appointment-tooltip">
                        <div className="tooltip-header">
                          <strong>{apt.is_group ? apt.group_name : (apt.patient ? apt.patient.fullname : 'Ingen patient')}</strong>
                          {apt.is_group ? (
                            <span className="tooltip-pnr"><i className="fas fa-users"></i> Gruppbokning</span>
                          ) : (
                            apt.patient && <span className="tooltip-pnr">{apt.patient.personnummer}</span>
                          )}
                        </div>
                        <div className="tooltip-row">
                          <i className="fas fa-clock"></i>
                          <span>
                            {new Date(apt.start_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                            {' - '}
                            {(() => {
                              // Använd beräknad sluttid för korrekt visning
                              const endHour = Math.floor(apt.endMinutes / 60);
                              const endMin = apt.endMinutes % 60;
                              return `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
                            })()}
                          </span>
                        </div>
                        {apt.appointment_type && (
                          <div className="tooltip-row">
                            <i className="fas fa-tag"></i>
                            <span>{apt.appointment_type.name}</span>
                          </div>
                        )}
                        {apt.user_name && (
                          <div className="tooltip-row">
                            <i className="fas fa-user-md"></i>
                            <span>{apt.user_name}</span>
                          </div>
                        )}
                        {bookedBy && (
                          <div className="tooltip-row tooltip-booked-by">
                            <i className="fas fa-user-edit"></i>
                            <span>{bookedBy}</span>
                          </div>
                        )}
                        {apt.is_group && (
                          <div className="tooltip-participants">
                            <div className="tooltip-participants-header">
                              <i className="fas fa-users"></i> Deltagare ({participantCount})
                            </div>
                            {participantCount > 0 ? (
                              <ul className="tooltip-participants-list">
                                {activeParticipants.map(p => (
                                  <li key={p.patient_id}>
                                    {p.patient?.fullname || p.fullname || 'Okänd patient'}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="tooltip-participants-empty">Inga deltagare än</div>
                            )}
                          </div>
                        )}
                        {apt.notes && (
                          <div className="tooltip-notes">
                            <i className="fas fa-sticky-note"></i>
                            <span>{apt.notes}</span>
                          </div>
                        )}
                        <div className="tooltip-status">
                          <span className={`status-badge status-${apt.status}`}>
                            {apt.status === 'scheduled' && 'Bokad'}
                            {apt.status === 'completed' && 'Genomförd'}
                            {apt.status === 'cancelled' && 'Avbokad'}
                            {apt.status === 'no_show' && 'Uteblev'}
                          </span>
                          {apt.is_paid && (
                            <span className="status-badge status-paid">
                              <i className="fas fa-check"></i> Betald ({apt.total_paid} kr)
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Dra i nederkanten för att ändra längd (Spår K3) */}
                      {isDraggable && (
                        <div
                          className="resize-handle"
                          title="Dra för att ändra längd"
                          onMouseDown={(e) => handleDragStart(e, 'appointment', apt, dayIndex, 'resize')}
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  );
                })}

                {/* Avbokade bokningar - en liten ikon per starttid (Spår S2) */}
                {positionedCancelled.map(group => (
                  <CancelledMarker
                    key={`cancelled-${group.startMinutes}`}
                    group={group}
                    top={(group.startMinutes - START_HOUR * 60) * PIXELS_PER_MINUTE}
                    onOpen={handleAppointmentDoubleClick}
                    onContextMenu={handleAppointmentContextMenu}
                  />
                ))}

                {/* Blocks/Notes renderade med absolut positionering */}
                {positionedBlocks.map(block => {
                  // Beräkna position baserat på minuter × vertikal skala
                  const topOffset = (block.startMinutes - START_HOUR * 60) * PIXELS_PER_MINUTE;
                  const bKey = blockKey(block);
                  const dragPreview = getDragPreview(bKey, 'block', block);
                  const resizeEnd = dragPreview?.mode === 'resize' ? dragPreview.end : null;
                  const durationMinutes = (resizeEnd ?? block.endMinutes) - block.startMinutes;
                  const height = Math.max(durationMinutes * PIXELS_PER_MINUTE - 2, 20);

                  // Kollisionslayout - samma som bokningar
                  const widthPercent = 100 / block.totalColumns;
                  const leftPercent = widthPercent * block.column;
                  const blockWidth = `calc(${widthPercent}% - ${4 + (block.cancelledGutter || 0)}px)`;
                  const blockLeft = `calc(${leftPercent}% + 2px)`;

                  const isRecurring = block.recurrence_pattern !== 'none' || block.is_recurring_instance;

                  // Ikoner och text baserat på block_type
                  const blockTypeInfo = {
                    block: { icon: 'fa-ban', label: 'Blockerar bokning' },
                    sick: { icon: 'fa-thermometer-half', label: 'Sjukdom' },
                    vacation: { icon: 'fa-umbrella-beach', label: 'Semester' },
                    note: { icon: 'fa-sticky-note', label: 'Endast anteckning' }
                  };
                  const typeInfo = blockTypeInfo[block.block_type] || blockTypeInfo.block;

                  // Drag-relaterat - blocks kan alltid dras
                  const dragOffset = getDragOffset(dragPreview);
                  const isBeingDragged = dragState.isDragging && dragState.itemId === bKey && dragState.itemType === 'block';
                  const dragOffsetX = isBeingDragged && dragState.mode !== 'resize' ? (dragState.currentX - dragState.startX) : 0;

                  return (
                    <div
                      key={`block-${block.block_id}-${block.start_time}`}
                      className={`calendar-block ${block.block_type} ${isRecurring ? 'recurring' : ''} draggable ${isBeingDragged ? 'dragging' : ''}`}
                      style={{
                        top: `${topOffset + dragOffset}px`,
                        height: `${height}px`,
                        width: blockWidth,
                        left: blockLeft,
                        transform: isBeingDragged ? `translateX(${dragOffsetX}px) scale(1.02)` : undefined,
                        transition: isBeingDragged ? 'none' : undefined,
                        pointerEvents: isBeingDragged ? 'none' : undefined,
                        zIndex: isBeingDragged ? 1000 : undefined
                      }}
                      onClick={(e) => {
                        if (justDraggedRef.current) return;
                        handleBlockClick(block, e);
                      }}
                      onContextMenu={(e) => handleBlockContextMenu(block, e)}
                      onMouseDown={(e) => handleDragStart(e, 'block', block, dayIndex)}
                    >
                      {dragPreview && (
                        <span className="drag-preview-label">{dragPreviewLabel(dragPreview)}</span>
                      )}
                      <div className="block-content">
                        <span
                          className="block-icon"
                          title={`${typeInfo.label}${block.blocks_booking ? '' : ' – går att boka på tiden'}`}
                        >
                          <i className={`fas ${typeInfo.icon}`}></i>
                        </span>
                        <span className="block-title">{block.title}</span>
                        {isRecurring && (
                          <span className="block-recurring" title="Återkommande">
                            <i className="fas fa-redo"></i>
                          </span>
                        )}
                      </div>
                      {/* Tooltip */}
                      <div className="block-tooltip">
                        <div className="tooltip-header">
                          <i className={`fas ${typeInfo.icon}`}></i>
                          <strong>{block.title}</strong>
                        </div>
                        <div className="tooltip-row">
                          <i className="fas fa-clock"></i>
                          <span>
                            {new Date(block.start_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                            {' - '}
                            {new Date(block.end_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        {block.user_name && (
                          <div className="tooltip-row">
                            <i className="fas fa-user-md"></i>
                            <span>{block.user_name}</span>
                          </div>
                        )}
                        {isRecurring && (
                          <div className="tooltip-row">
                            <i className="fas fa-redo"></i>
                            <span>
                              {block.recurrence_pattern === 'daily' && 'Varje dag'}
                              {block.recurrence_pattern === 'weekly' && 'Varje vecka'}
                              {block.recurrence_pattern === 'biweekly' && 'Varannan vecka'}
                            </span>
                          </div>
                        )}
                        <div className={`tooltip-type ${block.block_type}`}>
                          {typeInfo.label}
                        </div>
                      </div>
                      {/* Längdgrepp bara för block inom en dag (flerdagars, t.ex. semester, redigeras i rutan) */}
                      {block.block_type !== 'holiday'
                        && String(block.start_time).slice(0, 10) === String(block.end_time).slice(0, 10) && (
                        <div
                          className="resize-handle"
                          title="Dra för att ändra längd"
                          onMouseDown={(e) => handleDragStart(e, 'block', block, dayIndex, 'resize')}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Slot-override-modal (lägg till/ändra schemaslot en dag) */}
      {slotModal && (
        <SlotOverrideModal
          mode={slotModal.mode}
          date={slotModal.date}
          userId={parseInt(selectedUserId)}
          slot={slotModal.slot}
          defaultStartTime={slotModal.defaultStartTime}
          appointmentTypes={appointmentTypes}
          onClose={() => setSlotModal(null)}
          onSaved={handleSlotModalSaved}
        />
      )}

      {/* Info-popup för klickad schemaslot */}
      {activeSlotInfo && (
        <>
          <div className="schedule-slot-info-backdrop" onClick={() => setActiveSlotInfo(null)} />
          <div
            className="schedule-slot-info"
            style={{ top: `${activeSlotInfo.y + 10}px`, left: `${activeSlotInfo.x + 10}px` }}
          >
            <div className="ssi-header">
              <span
                className="ssi-dot"
                style={{ backgroundColor: activeSlotInfo.slot.appointment_type?.color || '#6c757d' }}
              />
              <strong>{activeSlotInfo.slot.appointment_type?.name || 'Schemaslot'}</strong>
            </div>
            <div className="ssi-time">
              <i className="fas fa-clock"></i> {activeSlotInfo.slot.start_time}–{activeSlotInfo.slot.end_time}
            </div>
            {activeSlotInfo.slot.template_name && (
              <div className="ssi-template">
                <i className="fas fa-calendar-week"></i> {activeSlotInfo.slot.template_name}
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal för att skapa/redigera bokning */}
      {showModal && (
        <AppointmentModal
          appointment={selectedAppointment}
          initialDate={selectedSlot}
          appointmentTypes={appointmentTypes}
          selectedUserId={selectedUserId}
          clinicUsers={clinicUsers}
          preSelectedType={preSelectedSlotType}
          onClose={handleCloseModal}
          onSave={handleSaveAppointment}
          onEndSeries={handleEndSeries}
          onCancel={handleCancelAppointment}
          onOpenPaymentModal={handleOpenPaymentModal}
          onOpenKallelseModal={handleOpenKallelseModal}
          onOpenGroupParticipants={handleOpenGroupParticipants}
        />
      )}

      {/* Högerklicksmeny */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="calendar-context-menu"
          style={{
            position: 'fixed',
            top: contextMenuPos.top,
            left: contextMenuPos.left,
            zIndex: 10000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Visa bokning-specifika alternativ om det finns en bokning */}
          {contextMenu.appointment ? (
            <>
              <div
                className="context-menu-item"
                onClick={() => handleContextMenuAction('open')}
              >
                <i className="fas fa-folder-open"></i>
                <span>Öppna bokning</span>
              </div>
              <div
                className="context-menu-item"
                onClick={() => handleContextMenuAction('reschedule')}
              >
                <i className="fas fa-calendar-alt"></i>
                <span>Omboka tid</span>
              </div>
              {contextMenu.appointment.patient && (
                <div
                  className="context-menu-item"
                  onClick={() => handleContextMenuAction('bookNew')}
                >
                  <i className="fas fa-calendar-plus"></i>
                  <span>Boka ny tid</span>
                </div>
              )}
              <div
                className="context-menu-item"
                onClick={() => handleContextMenuAction('copy')}
              >
                <i className="fas fa-copy"></i>
                <span>Kopiera</span>
              </div>
              {contextMenu.appointment.status !== 'cancelled' && (
                <div
                  className="context-menu-item context-menu-item-danger"
                  onClick={() => handleContextMenuAction('cancel')}
                >
                  <i className="fas fa-times-circle"></i>
                  <span>Avboka tid</span>
                </div>
              )}
              {/* Bokningar tas aldrig bort (Spår K1) - en serie avslutas i stället */}
              {isSeriesAppointment(contextMenu.appointment) && (
                <div
                  className="context-menu-item context-menu-item-danger"
                  onClick={() => handleContextMenuAction('endSeries')}
                >
                  <i className="fas fa-calendar-times"></i>
                  <span>Avsluta serien från och med…</span>
                </div>
              )}
              {!contextMenu.appointment.is_paid && contextMenu.appointment.status !== 'cancelled' && (
                <div
                  className="context-menu-item context-menu-item-success"
                  onClick={() => handleContextMenuAction('payment')}
                >
                  <i className="fas fa-cash-register"></i>
                  <span>Registrera betalning</span>
                </div>
              )}
              {contextMenu.appointment.is_paid && (
                <div className="context-menu-item context-menu-item-info">
                  <i className="fas fa-check-circle"></i>
                  <span>Betald ({contextMenu.appointment.total_paid} kr)</span>
                </div>
              )}
              {/* Uteblivet: avgift i kassan + röd cell + fakturaunderlag (gruppers deltagare hanteras i deltagarlistan) */}
              {!contextMenu.appointment.is_group && !contextMenu.appointment.is_paid &&
                !['cancelled', 'no_show'].includes(contextMenu.appointment.status) && (
                <div
                  className="context-menu-item context-menu-item-danger"
                  onClick={() => handleContextMenuAction('noShow')}
                >
                  <i className="fas fa-user-slash"></i>
                  <span>Registrera som uteblivet</span>
                </div>
              )}
              {/* Gruppbokning-alternativ */}
              {contextMenu.appointment.is_group && (
                <div
                  className="context-menu-item"
                  onClick={() => handleContextMenuAction('manageParticipants')}
                >
                  <i className="fas fa-users"></i>
                  <span>Hantera deltagare</span>
                </div>
              )}
              {/* Journal-alternativ (endast om patienten finns OCH kliniken har inbyggd journalmodul) */}
              {contextMenu.appointment.patient && useInternalJournal && (
                <>
                  <div className="context-menu-divider"></div>
                  <div
                    className="context-menu-item"
                    onClick={() => handleContextMenuAction('openJournal')}
                  >
                    <i className="fas fa-file-medical"></i>
                    <span>Öppna journal</span>
                  </div>
                  <div
                    className="context-menu-item"
                    onClick={() => handleContextMenuAction('newJournal')}
                  >
                    <i className="fas fa-file-medical-alt"></i>
                    <span>Ny journal</span>
                  </div>
                  <div
                    className="context-menu-item"
                    onClick={() => handleContextMenuAction('startRecording')}
                  >
                    <i className="fas fa-microphone"></i>
                    <span>Starta inspelning</span>
                  </div>
                </>
              )}
              <div className="context-menu-divider"></div>
            </>
          ) : null}

          {/* Visa block/note-specifika alternativ om det finns ett block */}
          {contextMenu.block ? (
            <>
              <div
                className="context-menu-item"
                onClick={() => handleContextMenuAction('openBlock')}
              >
                <i className={`fas ${
                  contextMenu.block.block_type === 'sick' ? 'fa-thermometer-half' :
                  contextMenu.block.block_type === 'vacation' ? 'fa-umbrella-beach' :
                  contextMenu.block.block_type === 'note' ? 'fa-sticky-note' : 'fa-ban'
                }`}></i>
                <span>Redigera {
                  contextMenu.block.block_type === 'sick' ? 'sjukdom' :
                  contextMenu.block.block_type === 'vacation' ? 'semester' :
                  contextMenu.block.block_type === 'note' ? 'anteckning' : 'blockering'
                }</span>
              </div>
              <div
                className="context-menu-item context-menu-item-danger"
                onClick={() => handleContextMenuAction('deleteBlock')}
              >
                <i className="fas fa-trash"></i>
                <span>Ta bort {
                  contextMenu.block.block_type === 'sick' ? 'sjukdom' :
                  contextMenu.block.block_type === 'vacation' ? 'semester' :
                  contextMenu.block.block_type === 'note' ? 'anteckning' : 'blockering'
                }</span>
              </div>
              <div className="context-menu-divider"></div>
            </>
          ) : null}

          {/* Schemaslot-alternativ (endast utan bokning/block) */}
          {!contextMenu.appointment && !contextMenu.block && (
            contextMenu.scheduleSlot ? (
              <>
                <div
                  className="context-menu-item context-menu-item-success"
                  onClick={() => handleContextMenuAction('bookSlot')}
                >
                  <i className="fas fa-user-plus"></i>
                  <span>
                    Boka {contextMenu.scheduleSlot.appointment_type?.name || 'på slot'}
                    {' '}({contextMenu.scheduleSlot.start_time}–{contextMenu.scheduleSlot.end_time})
                  </span>
                </div>
                <div
                  className="context-menu-item"
                  onClick={() => handleContextMenuAction('editSlot')}
                >
                  <i className="fas fa-edit"></i>
                  <span>Ändra schemaslot</span>
                </div>
                <div
                  className="context-menu-item context-menu-item-danger"
                  onClick={() => handleContextMenuAction('removeSlot')}
                >
                  <i className="fas fa-calendar-minus"></i>
                  <span>Ta bort schemaslot</span>
                </div>
                <div className="context-menu-divider"></div>
              </>
            ) : (
              selectedUserId && (
                <>
                  <div
                    className="context-menu-item"
                    onClick={() => handleContextMenuAction('addSlot')}
                  >
                    <i className="fas fa-calendar-plus"></i>
                    <span>Lägg schemaslot här</span>
                  </div>
                  <div className="context-menu-divider"></div>
                </>
              )
            )
          )}

          {/* Klistra in (Spår K4) - bara på tom tid och när något är kopierat */}
          {!contextMenu.appointment && !contextMenu.block && getCopiedAppointment() && selectedUserId && (
            <div
              className="context-menu-item context-menu-item-success"
              onClick={() => handleContextMenuAction('paste')}
            >
              <i className="fas fa-paste"></i>
              <span>Klistra in: {copiedLabel()}</span>
            </div>
          )}

          {/* Allmänna alternativ */}
          <div
            className="context-menu-item"
            onClick={() => handleContextMenuAction('book')}
          >
            <i className="fas fa-user-plus"></i>
            <span>Boka patient</span>
          </div>
          <div
            className="context-menu-item"
            onClick={() => handleContextMenuAction('block')}
          >
            <i className="fas fa-ban"></i>
            <span>Blocka tid</span>
          </div>
          <div
            className="context-menu-item"
            onClick={() => handleContextMenuAction('note')}
          >
            <i className="fas fa-sticky-note"></i>
            <span>Lägg till anteckning</span>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentAppointment && (
        <PaymentModal
          appointment={paymentAppointment}
          onClose={handleClosePaymentModal}
          onSave={handlePaymentSaved}
        />
      )}

      {/* Kallelse Modal */}
      {showKallelseModal && kallelseAppointment && (
        <KallelseModal
          appointment={kallelseAppointment}
          preSelectedTemplate={preSelectedKallelseTemplate}
          onClose={handleCloseKallelseModal}
        />
      )}

      {/* Block/Note Modal */}
      {showBlockModal && (
        <BlockNoteModal
          mode={blockModalMode}
          initialDate={selectedSlot}
          existingBlock={selectedBlock}
          selectedUserId={selectedUserId}
          clinicUsers={clinicUsers}
          onClose={handleCloseBlockModal}
          onSave={handleBlockSaved}
        />
      )}

      {/* Group Participants Modal */}
      {groupParticipantsModal}

      {/* Available Slot Search Modal */}
      {showSlotSearch && (
        <AvailableSlotSearch
          onSelectSlot={handleSlotSelected}
          onClose={() => setShowSlotSearch(false)}
        />
      )}

      {/* Behandlare Sjuk Modal */}
      {showBehandlareSjukModal && (
        <BehandlareSjukModal
          selectedUserId={selectedCalendarDisabled ? '' : selectedUserId}
          selectedDate={formatLocalDate(currentWeek)}
          clinicUsers={clinicUsers.filter(u => u.has_calendar !== false)}
          onClose={() => setShowBehandlareSjukModal(false)}
        />
      )}
    </div>
  );
};

export default Calendar;
